<template>
  <div>
    <CheckoutBillingAddressForm
      :address="formValues"
      :api-errors
      :countries
      :submitting
      @submit="handleSubmit"
      @submit-failed="handleFailure"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import type { Bill } from '@backmarket/nuxt-layer-checkout/api/cart/types'
import { useAddressStore } from '@backmarket/nuxt-layer-checkout/stores/addressStore'
import { useLogUnhandledAPIErrors } from '@backmarket/nuxt-module-address/useLogUnhandledAPIErrors'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import type { ErrorsOf } from '@ds/components/Form'

import useHandleUnauthorizedUser from '~/scopes/checkout/composables/useHandleUnauthorizedUser'
import type { FormValues } from '~/scopes/checkout/flows/e-commerce/pages/AddressesStep/components/BillingAddressForm/BillingAddressForm.types'
import CheckoutBillingAddressForm from '~/scopes/checkout/flows/e-commerce/pages/AddressesStep/components/BillingAddressForm/BillingAddressForm.vue'
import { getAPIErrors } from '~/scopes/checkout/utils/getAPIErrors'
import { GIFT_CARD_BILLABLE_COUNTRIES } from '~/scopes/gift-cards/constant'

const tracking = useTracking()
const logger = useLogger()
const addressStore = useAddressStore()

const { openErrorToast } = useTheToast()
const { logUnhandledAPIErrors } = useLogUnhandledAPIErrors()
const { handleUnauthorizedUser } = useHandleUnauthorizedUser()
const apiErrors = ref<{ [key: string]: string }>({})
const submitting = ref(false)

const props = defineProps<{
  billingAddress: Bill | null
}>()

const defaultAddress: Bill = {
  birthdate: null,
  city: null,
  company: null,
  country: null,
  countryDialInCode: null,
  customerIdNumber: null,
  dateCreation: null,
  dateModification: null,
  email: null,
  firstName: null,
  firstNamePronunciation: null,
  lastName: null,
  lastNamePronunciation: null,
  phone: null,
  postalCode: null,
  stateOrProvince: null,
  street: null,
  street2: null,
}

const formValues = computed(() => {
  if (!props.billingAddress) {
    return defaultAddress
  }

  return props.billingAddress
})

const countries = computed(() => {
  return [...GIFT_CARD_BILLABLE_COUNTRIES]
    .map(({ name, countryCode: billableCountryCode }) => ({
      value: billableCountryCode,
      label: name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
})

const emit = defineEmits<{
  (e: 'update'): void
}>()

const handleFailure = (errors: ErrorsOf<Omit<FormValues, 'nationalId'>>) => {
  logger.info('[CHECKOUT] Failed to submit billing form', {
    errors: { fields: Object.keys(errors) },
    owners: ['bot-squad-checkout-front'],
  })
  tracking.trackClick({
    name: 'billing_address_saved_failure',
    zone: 'address',
  })
}

const handleSubmit = async (values: Omit<FormValues, 'nationalId'>) => {
  apiErrors.value = {}
  submitting.value = true

  try {
    // TODO: check why we rely on shipping address to get the country dial in code and phone
    const address = {
      ...(props.billingAddress || defaultAddress),
      ...values,
    }

    // 1. Save address
    await addressStore.saveAddress({
      address,
      isShipping: false,
      isBilling: true,
    })

    tracking.trackClick({
      name: 'billing_address_saved',
      zone: 'address',
    })

    emit('update')
    submitting.value = false
  } catch (error) {
    tracking.trackClick({
      name: 'billing_address_saved_failure',
      zone: 'address',
    })

    if (
      (error as Record<string, unknown>)?.status === 400 ||
      (error as Record<string, unknown>)?.status === 422
    ) {
      const errors = getAPIErrors(error as Record<string, unknown>)
      logUnhandledAPIErrors(Object.keys(errors))
      apiErrors.value = errors
      openErrorToast()
    } else
      await handleUnauthorizedUser(
        error as Record<string, unknown>,
        '[CHECKOUT] Failed to submit billing form',
      )

    submitting.value = false
  }
}
</script>
